// frontend/src/pages/DriverService.jsx
import React, { useState } from "react";
import { Clock,Sun,MapPin,Shield,CheckCircle,ArrowRight,Calendar,Award,Navigation,Sparkles } from "lucide-react";

const DriverService = ({setForm}) => {
    const [selectedTab, setSelectedTab] = useState("local");

    // Local Tariff Data
    const localTariff = [
        {
            vehicle: "Manual/Auto Car",
            base4hrs: "₹500",
            extraHr: "₹100",
            icon: "🚗",
            color: "from-blue-500 to-cyan-500",
        },
        {
            vehicle: "Premium/Tata Ace/Dost/Bolero",
            base4hrs: "₹600",
            extraHr: "₹100",
            icon: "🚙",
            color: "from-emerald-500 to-teal-500",
        },
        {
            vehicle: "Tempo Traveller",
            base4hrs: "₹650",
            extraHr: "₹100",
            icon: "🚌",
            color: "from-purple-500 to-pink-500",
        },
        {
            vehicle: "Eicher/Heavy Vehicle",
            base4hrs: "₹700–₹800",
            extraHr: "₹100–₹150",
            icon: "🚛",
            color: "from-orange-500 to-amber-500",
        },
    ];

    // Local Drop Rates
    const localDropRates = [
        {
            vehicle: "Night Car Drop",
            price: "₹600",
            icon: "🌙",
            color: "from-indigo-500 to-blue-500",
        },
        {
            vehicle: "Tata Ace/Dost/Bolero",
            price: "₹700",
            icon: "🚚",
            color: "from-green-500 to-emerald-500",
        },
        {
            vehicle: "Tempo Traveller",
            price: "₹750",
            icon: "🚐",
            color: "from-purple-500 to-violet-500",
        },
        {
            vehicle: "Eicher",
            price: "₹800",
            icon: "🚛",
            color: "from-orange-500 to-red-500",
        },
        {
            vehicle: "Heavy Vehicle",
            price: "₹1000",
            icon: "🚚",
            color: "from-gray-500 to-gray-700",
        },
    ];

    // Outstation Duty Rates
    const outstationRates = [
        {
            type: "Car",
            sameDay: "₹1400",
            extraHr: "₹100",
            extraDay: "₹1500/day",
            icon: "🚗",
            color: "from-blue-500 to-cyan-500",
        },
        {
            type: "Commercial (Badge)",
            sameDay: "₹1600",
            extraHr: "₹100",
            extraDay: "₹1700/day",
            icon: "🏢",
            color: "from-emerald-500 to-teal-500",
        },
        {
            type: "Heavy Vehicle",
            sameDay: "₹1800",
            extraHr: "₹150",
            extraDay: "₹2000/day",
            icon: "🚛",
            color: "from-orange-500 to-amber-500",
        },
    ];

    const features = [
        {
            icon: <Shield className="h-5 w-5" />,
            text: "Verified & Professional Drivers",
            desc: "Background checked and experienced",
        },
        {
            icon: <Clock className="h-5 w-5" />,
            text: "24/7 Availability",
            desc: "Round the clock service",
        },
        {
            icon: <Award className="h-5 w-5" />,
            text: "Trained Experts",
            desc: "Route knowledge & safety trained",
        },
        {
            icon: <MapPin className="h-5 w-5" />,
            text: "All India Service",
            desc: "Available across India",
        },
    ];

    const benefits = [
        "No need for rental cars - Use your own vehicle",
        "Professional drivers with valid licenses",
        "Flexible hours - 4hrs, 8hrs, 12hrs, or full day",
        "Airport drops, city tours, outstation trips",
        "Commercial & heavy vehicle drivers available",
        "Immediate service - Book and get driver within 30 mins",
    ];

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative py-12 md:py-20">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full text-white mb-6 shadow-lg">
                                <Sparkles className="h-4 w-4 mr-2" />
                                <span className="text-sm font-semibold">
                                    24/7 PROFESSIONAL DRIVER SERVICE
                                </span>
                            </div>

                            <h1 className="text-4xl md:text-5xl font-bold mb-6">
                                <span className="block bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 bg-clip-text text-transparent">
                                    Need a Professional
                                </span>
                                <span className="block bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 bg-clip-text text-transparent mt-2">
                                    Acting Driver?
                                </span>
                            </h1>

                            <p className="text-xl text-white mb-10 max-w-2xl mx-auto">
                                Experienced drivers for your personal or
                                commercial vehicles. Available 24/7 for local
                                and outstation trips.
                            </p>
                        </div>

                        {/* Features */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
                            {features.map((feature, index) => (
                                <div
                                    key={index}
                                    className="bg-white rounded-xl p-6 shadow-lg border border-emerald-100 hover:shadow-xl transition-all duration-300"
                                >
                                    <div className="p-3 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                                        <div className="text-emerald-600">
                                            {feature.icon}
                                        </div>
                                    </div>
                                    <h3 className="font-bold text-gray-900 mb-1">
                                        {feature.text}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {feature.desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="relative py-12">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        {/* Tabs Navigation */}
                        <div className="flex flex-col justify-center md:flex-row gap-4 mb-12">
                            <button
                                onClick={() => setSelectedTab("local")}
                                className={`px-6 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center ${
                                    selectedTab === "local"
                                        ? "bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-xl transform scale-105"
                                        : "bg-white text-gray-700 shadow-lg hover:shadow-xl"
                                }`}
                            >
                                <Sun className="h-5 w-5 mr-2" />
                                Local Tariff (4 Hrs Package)
                            </button>
                            <button
                                onClick={() => setSelectedTab("drop")}
                                className={`px-6 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center ${
                                    selectedTab === "drop"
                                        ? "bg-gradient-to-br from-blue-500 to-cyan-500 text-white shadow-xl transform scale-105"
                                        : "bg-white text-white-700 shadow-lg hover:shadow-xl"
                                }`}
                            >
                                <MapPin className="h-5 w-5 mr-2" />
                                Local Drop (Min 2 Hrs)
                            </button>
                            <button
                                onClick={() => setSelectedTab("outstation")}
                                className={`px-6 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center ${
                                    selectedTab === "outstation"
                                        ? "bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-xl transform scale-105"
                                        : "bg-white text-gray-700 shadow-lg hover:shadow-xl"
                                }`}
                            >
                                <Navigation className="h-5 w-5 mr-2" />
                                Outstation Duty (12 Hrs)
                            </button>
                        </div>

                        {/* Local Tariff Section */}
                        {selectedTab === "local" && (
                            <div className="animate-fadeIn">
                                <div className="text-center mb-10">
                                    <h2 className="text-3xl font-bold text-white mb-4">
                                        Local Tariff - 4 Hours Package
                                    </h2>
                                    <p className="text-white max-w-2xl mx-auto">
                                        Perfect for daily commuting, shopping
                                        trips, and city tours
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                                    {localTariff.map((item, index) => (
                                        <div
                                            key={index}
                                            className="bg-white rounded-2xl shadow-xl border border-gray-200 hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 p-6"
                                        >
                                            <div
                                                className={`h-2 w-full bg-gradient-to-r ${item.color} rounded-t-xl -mt-6 mb-6`}
                                            ></div>
                                            <div className="text-4xl mb-4">
                                                {item.icon}
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900 mb-4">
                                                {item.vehicle}
                                            </h3>

                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg">
                                                    <div className="flex items-center">
                                                        <Clock className="h-4 w-4 text-emerald-600 mr-2" />
                                                        <span className="text-gray-700">
                                                            Base (4 Hrs)
                                                        </span>
                                                    </div>
                                                    <div className="text-2xl font-bold text-emerald-700">
                                                        {item.base4hrs}
                                                    </div>
                                                </div>

                                                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg">
                                                    <div className="flex items-center">
                                                        <Clock className="h-4 w-4 text-blue-600 mr-2" />
                                                        <span className="text-gray-700">
                                                            Extra Hour
                                                        </span>
                                                    </div>
                                                    <div className="text-xl font-bold text-blue-700">
                                                        {item.extraHr}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Book button */}
                                <div className="flex align-center justify-center">
                                    <button onClick={() => setForm(true)}
                                        className="
                                  group relative px-8 py-3 rounded-full
                                  bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full text-white mb-6 shadow-lg
                                  text-white font-extrabold tracking-wide
                                  shadow-xl shadow-pink-500/30
                                  overflow-hidden
                                  transition-all duration-300
                                  hover:shadow-pink-500/50
                                  hover:-translate-y-0.5"
                                    >
                                        {/* Light sweep */}
                                        <span
                                            className="
                                  absolute inset-0 bg-white/25
                                  translate-x-[-100%] group-hover:translate-x-[100%]
                                  transition-transform duration-700 skew-x-12"
                                        ></span>

                                        <span className="relative flex items-center">
                                            BOOK Driver Now
                                            <ArrowRight
                                                className="
                                    ml-2 h-4 w-4
                                    transition-transform duration-300
                                    group-hover:translate-x-1"
                                            />
                                        </span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Local Drop Section */}
                        {selectedTab === "drop" && (
                            <div className="animate-fadeIn">
                                <div className="text-center mb-10">
                                    <h2 className="text-3xl font-bold text-white mb-4">
                                        Local Drop - Minimum 2 Hours
                                    </h2>
                                    <p className="text-white max-w-2xl mx-auto">
                                        Quick drop services for short trips and
                                        airport transfers
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-10">
                                    {localDropRates.map((item, index) => (
                                        <div
                                            key={index}
                                            className="bg-white rounded-2xl shadow-xl border border-gray-200 hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 p-6"
                                        >
                                            <div
                                                className={`h-2 w-full bg-gradient-to-r ${item.color} rounded-t-xl -mt-6 mb-6`}
                                            ></div>
                                            <div className="text-4xl mb-4">
                                                {item.icon}
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
                                                {item.vehicle}
                                            </h3>

                                            <div className="text-center">
                                                <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2">
                                                    {item.price}
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    Per Drop
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex align-center justify-center">
                                    <button onClick={() => setForm(true)}
                                        className="
                                  group relative px-8 py-3 rounded-full
                                  bg-gradient-to-br from-blue-500 to-cyan-500 text-white shadow-xl
                                  text-white font-extrabold tracking-wide
                                  shadow-xl shadow-pink-500/30
                                  overflow-hidden
                                  transition-all duration-300
                                  hover:shadow-pink-500/50
                                  hover:-translate-y-0.5"
                                    >
                                        {/* Light sweep */}
                                        <span
                                            className="
                                  absolute inset-0 bg-white/25
                                  translate-x-[-100%] group-hover:translate-x-[100%]
                                  transition-transform duration-700 skew-x-12"
                                        ></span>

                                        <span className="relative flex items-center">
                                            BOOK Driver Now
                                            <ArrowRight
                                                className="
                                    ml-2 h-4 w-4
                                    transition-transform duration-300
                                    group-hover:translate-x-1"
                                            />
                                        </span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Outstation Duty Section */}
                        {selectedTab === "outstation" && (
                            <div className="animate-fadeIn">
                                <div className="text-center mb-10">
                                    <div className="inline-flex items-center px-4 py-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full text-white mb-4 shadow-lg">
                                        <Navigation className="h-4 w-4 mr-2" />
                                        <span className="text-sm font-semibold">
                                            SOUTH INDIA OUTSTATION DUTY
                                        </span>
                                    </div>
                                    <h2 className="text-3xl font-bold text-white mb-4">
                                        Outstation Duty - 12 Hours Package
                                    </h2>
                                    <p className="text-white max-w-2xl mx-auto">
                                        Perfect for intercity trips, business
                                        travel, and family tours
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                                    {outstationRates.map((item, index) => (
                                        <div
                                            key={index}
                                            className="bg-white rounded-2xl shadow-xl border border-gray-200 hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 p-6"
                                        >
                                            <div
                                                className={`h-2 w-full bg-gradient-to-r ${item.color} rounded-t-xl -mt-6 mb-6`}
                                            ></div>
                                            <div className="text-4xl mb-4">
                                                {item.icon}
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                                                {item.type}
                                            </h3>

                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                                                    <div className="flex items-center">
                                                        <Sun className="h-4 w-4 text-purple-600 mr-2" />
                                                        <span className="text-gray-700">
                                                            Same Day
                                                        </span>
                                                    </div>
                                                    <div className="text-2xl font-bold text-purple-700">
                                                        {item.sameDay}
                                                    </div>
                                                </div>

                                                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg">
                                                    <div className="flex items-center">
                                                        <Clock className="h-4 w-4 text-blue-600 mr-2" />
                                                        <span className="text-gray-700">
                                                            Extra Hour
                                                        </span>
                                                    </div>
                                                    <div className="text-xl font-bold text-blue-700">
                                                        {item.extraHr}
                                                    </div>
                                                </div>

                                                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg">
                                                    <div className="flex items-center">
                                                        <Calendar className="h-4 w-4 text-emerald-600 mr-2" />
                                                        <span className="text-gray-700">
                                                            Extra Day
                                                        </span>
                                                    </div>
                                                    <div className="text-xl font-bold text-emerald-700">
                                                        {item.extraDay}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                  <div className="flex align-center justify-center">
                                    <button onClick={() => setForm(true)}
                                        className="
                                  group relative px-8 py-3 rounded-full
                                  bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-xl
                                  text-white font-extrabold tracking-wide
                                  shadow-xl shadow-pink-500/30
                                  overflow-hidden
                                  transition-all duration-300
                                  hover:shadow-pink-500/50
                                  hover:-translate-y-0.5"
                                    >
                                        {/* Light sweep */}
                                        <span
                                            className="
                                  absolute inset-0 bg-white/25
                                  translate-x-[-100%] group-hover:translate-x-[100%]
                                  transition-transform duration-700 skew-x-12"
                                        ></span>

                                        <span className="relative flex items-center">
                                            BOOK Driver Now
                                            <ArrowRight
                                                className="
                                    ml-2 h-4 w-4
                                    transition-transform duration-300
                                    group-hover:translate-x-1"
                                            />
                                        </span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Benefits Section */}
                        <div className="mt-16">
                            <div className="text-center mb-10">
                                <h2 className="text-3xl font-bold text-white mb-4">
                                    Why Choose Our Driver Service?
                                </h2>
                                <p className="text-white max-w-2xl mx-auto">
                                    Experience premium acting driver service
                                    with these benefits
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {benefits.map((benefit, index) => (
                                    <div
                                        key={index}
                                        className="bg-white rounded-xl p-6 shadow-lg border border-emerald-100 hover:shadow-xl transition-all duration-300"
                                    >
                                        <div className="flex items-start">
                                            <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg mr-3 mt-1">
                                                <CheckCircle className="h-4 w-4 text-white" />
                                            </div>
                                            <p className="text-gray-700 font-medium">
                                                {benefit}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Custom CSS */}
            <style jsx global>{`
                @keyframes blob {
                    0% {
                        transform: translate(0px, 0px) scale(1);
                    }
                    33% {
                        transform: translate(30px, -50px) scale(1.1);
                    }
                    66% {
                        transform: translate(-20px, 20px) scale(0.9);
                    }
                    100% {
                        transform: translate(0px, 0px) scale(1);
                    }
                }

                .animate-blob {
                    animation: blob 7s infinite;
                }

                .animation-delay-2000 {
                    animation-delay: 2s;
                }

                .animation-delay-4000 {
                    animation-delay: 4s;
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .animate-fadeIn {
                    animation: fadeIn 0.5s ease-out;
                }

                /* Custom scrollbar */
                ::-webkit-scrollbar {
                    width: 10px;
                }

                ::-webkit-scrollbar-track {
                    background: linear-gradient(to bottom, #f0fdf4, #ecfdf5);
                    border-radius: 5px;
                }

                ::-webkit-scrollbar-thumb {
                    background: linear-gradient(
                        to bottom,
                        #10b981,
                        #0d9488,
                        #0891b2
                    );
                    border-radius: 5px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(
                        to bottom,
                        #059669,
                        #0f766e,
                        #0e7490
                    );
                }
            `}</style>
        </div>
    );
};

export default DriverService;
